/**
 * Activity Logger - writes audit entries to activity_log table
 * Used for tracking user actions for FDA compliance (7-year retention)
 */
import { supabase } from '@/lib/supabase';

export type ActivityType =
  | 'user_login'
  | 'user_logout'
  | 'document_uploaded'
  | 'document_downloaded'
  | 'document_deleted'
  | 'document_renewed'
  | 'document_updated'
  | 'document_archived'
  | 'document_restored'
  | 'upload_link_created'
  | 'upload_link_deactivated'
  | 'link_completed'
  | 'requirement_satisfied'
  | 'requirement_unlocked'
  | 'organization_created'
  | 'organization_updated'
  | 'organization_deleted'
  | 'group_created'
  | 'group_updated'
  | 'group_deleted'
  | 'user_invited'
  | 'user_updated'
  | 'settings_updated'
  | 'document_type_created'
  | 'document_type_updated'
  | 'document_type_deleted'
  | 'organization_type_created'
  | 'organization_type_updated'
  | 'organization_type_deleted'
  | 'product_created'
  | 'product_updated'
  | 'product_deleted'
  | 'weekly_report_sent';

interface LogActivityParams {
  accountId: string;
  type: ActivityType;
  description: string;
  metadata?: Record<string, unknown>;
}

export async function logActivity({ accountId, type, description, metadata }: LogActivityParams) {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // supabase-js does NOT throw on a rejected insert (RLS, constraint, bad
    // column) — it returns `{ error }` and only throws on network failure.
    // Inspect `error` explicitly, mirroring `lib/adminAudit.ts`, so a silently
    // dropped FDA audit row surfaces in the console instead of vanishing.
    const { error } = await supabase.from('activity_log').insert({
      account_id: accountId,
      user_id: user?.id || null,
      type,
      description,
      metadata: metadata || {},
    });

    if (error) throw error;
  } catch (error) {
    // Don't throw - logging should never break the main flow
    console.error('Failed to log activity:', error);
  }
}
